import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import type { SemanticTone } from '@chicare/shared';

/**
 * Badge — a small, monospaced, uppercase piece of status.
 *
 * Monospace on purpose: badges usually carry a code, a count or a date
 * ("32 VERIFIED", "MAR 2028", "B24F0017"), and those are read by comparison
 * rather than by reading. Tabular figures make two badges stacked in a table
 * line up digit for digit.
 */

const tones: Record<SemanticTone, string> = {
  neutral: 'bg-paper text-muted',
  brand: 'bg-blue-tint text-blue',
  verified: 'bg-verified-tint text-verified',
  caution: 'bg-caution-tint text-caution',
  danger: 'bg-danger-tint text-danger',
};

export interface BadgeProps {
  tone?: SemanticTone;
  className?: string;
  children: ReactNode;
}

export function Badge({ tone = 'neutral', className, children }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap rounded-[5px] px-[7px] py-[3px]',
        'font-mono text-[10px] uppercase tracking-[0.05em] tabular',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
